import { useMemo, useState } from 'react';
import { Eye } from 'lucide-react';

import { DataTable, type Column, type FilterConfig } from '@/components/ui/data-table'
import { useChangeHistory } from '@/entities/history';

import {
  DialogViewDetails,
  type ChangeHistoryRow,
} from './ui/DialogViewDetails'

function buildFilters(users: string[]): FilterConfig[] {
  return [
    {
      type: 'date',
      key: 'startDate',
      label: 'From',
    },
    {
      type: 'date',
      key: 'endDate',
      label: 'To',
    },
    {
      type: 'select',
      key: 'createdBy',
      label: 'User',
      options: [
        { value: 'all', label: 'All Users' },
        ...users.map((u) => ({ value: u, label: u })),
      ],
      noFilterValue: 'all',
    },
    {
      type: 'select',
      key: 'action',
      label: 'Action',
      options: [
        { value: 'all', label: 'All' },
        { value: 'create', label: 'Create' },
        { value: 'update', label: 'Update' },
        { value: 'duplicate', label: 'Duplicate' },
        { value: 'delete', label: 'Delete' },
      ],
      noFilterValue: 'all',
    },
    {
      type: 'select',
      key: 'type',
      label: 'Entity',
      options: [
        { value: 'all', label: 'All' },
        { value: 'campaign', label: 'Campaign' },
        { value: 'llm', label: 'LLM' },
        { value: 'tool', label: 'Tool' },
      ],
      noFilterValue: 'all',
    },
  ]
}

function renderDateCell(value: string) {
  return (
    <span className="whitespace-nowrap">
      {new Date(value).toLocaleDateString()}{' '}
      {new Date(value).toLocaleTimeString()}
    </span>
  )
}

function renderEntityCell(row: ChangeHistoryRow) {
  let typeName = ''
  let entityInfo = ''

  switch (row.type) {
    case 'campaign':
      if (row.campaign_id) {
        typeName = 'Campaign'
        entityInfo = row.campaigns?.name
          ? `${row.campaign_id} ${row.campaigns.name}`
          : `${row.campaign_id}`
      }
      break
    case 'llm':
      if (row.llm_id) {
        typeName = 'LLM'
        entityInfo = row.llms?.name
          ? `${row.llm_id} ${row.llms.name}`
          : `${row.llm_id}`
      }
      break
    case 'tool':
      if (row.tool_id) {
        typeName = 'Tool'
        entityInfo = row.tools?.name
          ? `${row.tool_id} ${row.tools.name}`
          : `${row.tool_id}`
      }
      break
  }

  return typeName ? (
    <div>
      <div className="text-gray-600">{typeName}</div>
      <div>{entityInfo}</div>
    </div>
  ) : (
    '-'
  )
}

export function ChangeHistory() {
  const [selectedItem, setSelectedItem] = useState<ChangeHistoryRow | null>(
    null
  )
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    action: 'all',
    type: 'all',
    createdBy: 'all',
    startDate: '',
    endDate: '',
  })

  const { data, isLoading } = useChangeHistory({
    page: 0,
    pageSize: 500,
    orderBy: 'created_at',
    orderType: 'desc',
    action: filterValues.action,
    type: filterValues.type,
    startDate: filterValues.startDate || undefined,
    endDate: filterValues.endDate || undefined,
    createdBy: filterValues.createdBy,
  })

  const rows = data?.rows || []

  const uniqueUsers = useMemo(() => {
    const users = new Set(rows.map((r) => r.created_by).filter(Boolean))
    return Array.from(users).sort()
  }, [rows])

  const filters = useMemo(() => buildFilters(uniqueUsers), [uniqueUsers])

  const columns: Column<ChangeHistoryRow>[] = useMemo(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Date',
        cell: (value) => renderDateCell(value as string),
      },
      {
        accessorKey: 'created_by',
        header: 'User',
      },
      {
        accessorKey: 'type',
        header: 'Entity',
        cell: (_, row) => renderEntityCell(row),
      },
      {
        accessorKey: 'action',
        header: 'Action',
      },
      {
        accessorKey: 'id',
        header: 'Details',
        sortable: false,
        cell: (_, row) => (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setSelectedItem(row)
            }}
            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
            title="View all details"
            aria-label="View all details"
          >
            <Eye className="w-4 h-4" aria-hidden="true" />
          </button>
        ),
      },
    ],
    []
  )

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className="w-full h-full p-6 overflow-y-auto">
      <h1 className="text-heading font-bold text-gray-900 mb-6">Change History</h1>
      {isLoading ? (
        <p className="text-gray-500">Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          showDownload={false}
          filters={filters}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          defaultSort={{ field: 'created_at', direction: 'desc' }}
        />
      )}
      {selectedItem && (
        <DialogViewDetails
          dialogOpen={!!selectedItem}
          setDialogOpen={(open) => {
            if (!open) setSelectedItem(null)
          }}
          selectedItem={selectedItem}
          hasPromptEditor={false}
        />
      )}
    </div>
  )
}